import { useEffect, useRef, useState } from "react";
import {
  animate,
  motion,
  useAnimation,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from "framer-motion";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";

export const Section4 = () => {
  const topSentinelRef = useRef<HTMLDivElement | null>(null);

  const inView = useInView(topSentinelRef, {
    once: true,
    amount: 0,
    margin: "0px 0px -40% 0px",
  });

  const MOBILE = useResponsiveMobile(900);

  //  PC용 컨트롤러들
  const leftCtrl = useAnimation();
  const rightCtrl = useAnimation();
  const pcFillProgress = useMotionValue(0);
  const pcLinePct = useTransform(pcFillProgress, (v) => `${Math.round(v * 100)}%`);
  const pcStartedRef = useRef(false);
  const mobileLeftCtrl = useAnimation();
  const mobileLineWrapRef = useRef<HTMLDivElement | null>(null);
  const [mobileLineDone, setMobileLineDone] = useState(false);

  useEffect(() => {
    if (!inView || MOBILE) return;

    if (pcStartedRef.current) return;
    pcStartedRef.current = true;

    // 초기화
    leftCtrl.set({ opacity: 0, y: 24 });
    pcFillProgress.set(0);
    rightCtrl.set({
      opacity: 0,
      clipPath: "inset(0% 100% 0% 0%)",
      y: 0,
    });

    // 1️⃣ 왼쪽 텍스트 (빠르게)
    leftCtrl.start({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.55,
        ease: "easeOut",
      },
    });

    // 2️⃣ 라인: Section3과 동일하게 2초
    const controls = animate(pcFillProgress, 1, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
    });

    // 3️⃣ 오른쪽 텍스트: Section3과 동일 타이밍
    rightCtrl.start({
      opacity: 1,
      clipPath: "inset(0% 0% 0% 0%)",
      y: 0,
      transition: {
        delay: 1.4,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    });
    return () => controls.stop();
  }, [inView, MOBILE, leftCtrl, pcFillProgress, rightCtrl]);

  useEffect(() => {
    if (!inView || !MOBILE) return;

    mobileLeftCtrl.set({ opacity: 0, y: 24 });
    mobileLeftCtrl.start({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
      },
    });
  }, [inView, MOBILE, mobileLeftCtrl]);

  const { scrollYProgress: mobileLineProgress } = useScroll({
    target: mobileLineWrapRef,
    offset: ["start 50%", "end 50%"],
  });

  const mobileFill = useMotionValue(0);
  useMotionValueEvent(mobileLineProgress, "change", (p) => {
    const next = Math.max(0, Math.min(1, p));
    mobileFill.set(next);
    setMobileLineDone(next >= 0.75);
  });
  const mobileFillHeight = useTransform(mobileFill, (v) => `${v * 100}%`);

  useEffect(() => {
    if (MOBILE) {
      pcStartedRef.current = false;
      leftCtrl.set({ opacity: 0, y: 24 });
      pcFillProgress.set(0);
      rightCtrl.set({
        opacity: 0,
        clipPath: "inset(0% 100% 0% 0%)",
        y: 0,
      });

      const current = mobileLineProgress.get();
      const next = Math.max(0, Math.min(1, current));
      mobileFill.set(next);
      setMobileLineDone(next >= 0.75);
    } else {
      mobileLeftCtrl.set({ opacity: 0, y: 24 });
      mobileFill.set(0);
      setMobileLineDone(false);
    }
  }, [MOBILE, leftCtrl, mobileFill, mobileLeftCtrl, mobileLineProgress, pcFillProgress, rightCtrl]);

  return MOBILE ? (
    // ===================== MOBILE (기존 유지) =====================
    <section className="w-full flex justify-center items-center">
      <div className="flex flex-col w-full max-w-[1600px] relative">
        <div
          ref={topSentinelRef}
          className="absolute top-0 left-0 w-[1px] h-[1px] opacity-0 pointer-events-none"
        />

        <motion.div
          className="text-[36px] flex justify-center items-center font-bold leading-[1.15] text-[#34363F] pt-[20px]"
          initial={{ opacity: 0, y: 24 }}
          animate={mobileLeftCtrl}
        >
          <div className="flex flex-col gap-9">
            <span className="text-[#696E77] text-center">
              운영 효율화 <br />
              정산 자동화 <br />
              영업 관리까지
            </span>
            <span className="text-[#4D4F58] text-center">
              한번에 통합된 <br />
              SaaS 솔루션{" "}
            </span>
          </div>
        </motion.div>

        <div className="flex flex-col items-center gap-10 mt-10">
          <div ref={mobileLineWrapRef} className="h-[260px] w-[4px]">
            <div className="relative w-full h-full bg-[#DDE7FF] overflow-hidden rounded-full">
              <motion.div
                className="absolute left-0 top-0 w-full"
                style={{ background: "#0079FF", height: mobileFillHeight }}
              />
            </div>
          </div>

          <motion.span
            className="font-bold text-[36px] text-[#0079FF] text-center"
            initial={{ opacity: 0, y: 24 }}
            animate={mobileLineDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            이제 당신의
            <br />
            차례입니다.
          </motion.span>
        </div>
      </div>
    </section>
  ) : (
    // ===================== PC (요구 시퀀스 적용) =====================
    <section className="w-full flex justify-center items-center py-32 md:py-0 md:px-10 md:pb-[160px] relative ">
      {/*  센티넬은 section 안에서 absolute가 먹히려면 section이 relative여야 안정적 */}
      <div
        ref={topSentinelRef}
        className="absolute top-0 left-0 w-[1px] h-[1px] opacity-0 pointer-events-none"
      />

      <div className="w-full flex gap-[36px] max-w-[1440px] items-end justify-end">
        {/* 1) 왼쪽 텍스트 */}
        <motion.div
          className="text-display font-bold leading-[1.15] text-[#121319]"
          initial={{ opacity: 0, y: 24 }}
          animate={leftCtrl}
        >
          <span className="text-[#4D4F58]">운영 효율화, 정산 자동화,</span>
          <br />
          영업 관리까지 한 번에
          <br />
          통합된 SaaS 솔루션
        </motion.div>

        {/* 2) 라인 (Section3과 동일) */}
        <div className="flex flex-1 md:mb-[24px]">
          <div className="relative overflow-hidden rounded-full w-full h-[4px]">
            <div className="absolute inset-0 bg-[#D1D5DB]" />
            <motion.div
              className="absolute inset-y-0 left-0"
              style={{
                width: pcLinePct,
                height: "100%",
                background: "linear-gradient(90deg, rgba(0,104,255,0) 0%, #0068FF 100%)",
              }}
            />
          </div>
        </div>

        {/* 3) 오른쪽 텍스트 (Section3과 동일 타이밍) */}
        <motion.span
          className="font-bold text-display text-[#0079FF] whitespace-nowrap"
          initial={{ opacity: 0, clipPath: "inset(0% 100% 0% 0%)", y: 0 }}
          animate={rightCtrl}
        >
          이제 당신의 차례입니다.
        </motion.span>
      </div>
    </section>
  );
};
